import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, useLocalState } from '@freelancelabs/components';

import { ActivationError, ActivationRefusal, ActivationResult, activateAccount } from '../api/activateAccount';
import PublicLayout from '../app/content/PublicLayout';
import SetPasswordForm from '../app/login/SetPasswordForm';
import { PublicRoutes, defaultUsernameKey } from '../constants';

/**
 * Setting a password from an invitation link: `/activation?token=…`, the address
 * `client-hub-api` builds from `PORTAL_BASE_URL`.
 *
 * **Attention, A token cannot be checked before it is spent.** There is no query that says
 * whether a link is still good: `activateAccount` validates the token, sets the
 * password and consumes the invitation in one call. So the screen does not pretend
 * to verify anything on load: the only check it can make without the network is
 * whether a token is in the address at all. « Verifying » is therefore the state of
 * the submission, not of the page opening, and a spinner on arrival would have been
 * a spinner over nothing.
 *
 * The form itself is `SetPasswordForm`, unchanged and shared with « Change your
 * password »: two fields, the shared password rule and the checklist under the
 * field. Only what happens on submit differs, which is exactly the seam that
 * component already offers.
 */
const ActivationPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [, setDefaultUsername] = useLocalState('', defaultUsernameKey);

    const [result, setResult] = useState<ActivationResult | null>(null);
    const [refusal, setRefusal] = useState<ActivationRefusal | null | undefined>(undefined);

    /* ------------------------------------------------------------------ success */
    if (result) {
        const accounts = result.organizationNames;
        // Joined outside the template: ttag refuses a call expression inside a
        // localised string, and the extraction fails rather than the build.
        const accountList = accounts.join(', ');
        return (
            <PublicLayout>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`Your account is ready`}
                </h1>
                <span className="text-sm pb-4">
                    {c('Activation')
                        .t`Your password is set. You can now sign in with your email address and this password.`}
                </span>
                {accounts.length > 0 && (
                    <span className="text-sm text-text-hint pb-2">
                        {ngettext(
                            msgid`This gives you access to ${accounts.length} account: ${accountList}.`,
                            `This gives you access to ${accounts.length} accounts: ${accountList}.`,
                            accounts.length
                        )}
                    </span>
                )}
                {/* The person is carried to the sign-in screen rather than left to
                    find it, and their address travels with them: the login form
                    reads the same key, so it opens filled in. */}
                <Link to={PublicRoutes.LOGIN} className="mt-4">
                    <Button size="large" fullWidth>
                        {c('Activation action').t`Sign in`}
                    </Button>
                </Link>
            </PublicLayout>
        );
    }

    /* ------------------------------------------------------------------ refusals */
    // `undefined` means « nothing refused yet ». `null` means « refused, and the API
    // did not name it »: an unexpected failure, which is not the same thing as a
    // link problem and must not be worded as one.
    const terminal = refusal !== undefined && refusal !== ActivationRefusal.PASSWORD_REJECTED;
    if (!token || terminal) {
        return (
            <PublicLayout>
                <RefusalPanel
                    refusal={token ? refusal : ActivationRefusal.INVALID_LINK}
                    onRetry={() => setRefusal(undefined)}
                />
            </PublicLayout>
        );
    }

    /* ---------------------------------------------------------------- the form */
    return (
        <PublicLayout>
            <h1 className="text-xl font-semibold text-text-norm">{c('Activation title').t`Choose your password`}</h1>
            <span className="text-sm text-text-hint pb-6">
                {c('Activation')
                    .t`This link opens your Freelance.com client area. Choose a password to finish setting up your access.`}
            </span>

            {/* Stays above the form, because this refusal is repaired IN the form:
                moving to a full-page message would hide the very field to change. */}
            {refusal === ActivationRefusal.PASSWORD_REJECTED && (
                <Alert type="danger" className="mb-4">
                    {c('Activation refusal')
                        .t`This password was refused by our security policy. Choose a different one.`}
                </Alert>
            )}

            <SetPasswordForm
                onSubmit={async (password) => {
                    setRefusal(undefined);
                    try {
                        const activated = await activateAccount(token, password);
                        if (activated.email) {
                            setDefaultUsername(activated.email);
                        }
                        setResult(activated);
                    } catch (error) {
                        setRefusal(error instanceof ActivationError ? error.code : null);
                        // Rethrown so the form drops its loading state; the message
                        // is already on screen, chosen by the code above.
                        throw error;
                    }
                }}
            />
        </PublicLayout>
    );
};

interface RefusalPanelProps {
    refusal: ActivationRefusal | null | undefined;
    onRetry: () => void;
}

/**
 * What to say, and what to offer, for each refusal.
 *
 * **Attention, only one of them is repaired by trying again.** Offering « retry » to someone
 * holding an expired link, or a revoked one, is a loop: the button would do exactly
 * what just failed. Those refusals therefore carry no button at all, or the button
 * that actually leads somewhere.
 *
 * **Attention, an expired link cannot be renewed from this screen.** `resendInvitation`
 * takes a `userId` and refuses without `MISSING_PERMISSION` / `FORBIDDEN_OUT_OF_SCOPE`
 *: it is an operator's gesture from the back-office, and the person reading this
 * page is anonymous and does not know their own identifier. So the screen names who
 * can help rather than showing a button that would refuse.
 */
const RefusalPanel = ({ refusal, onRetry }: RefusalPanelProps) => {
    const signIn = (
        <Link to={PublicRoutes.LOGIN} className="mt-6 block">
            <Button size="large" fullWidth>
                {c('Activation action').t`Sign in`}
            </Button>
        </Link>
    );

    if (refusal === ActivationRefusal.ALREADY_ACTIVATED) {
        return (
            <>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`This account is already active`}
                </h1>
                <span className="text-sm">
                    {c('Activation refusal')
                        .t`This invitation has already been used. Sign in with the password you chose.`}
                </span>
                {signIn}
            </>
        );
    }

    if (refusal === ActivationRefusal.IDENTITY_UNAVAILABLE) {
        // The one refusal the API names as transient, so the screen can say plainly
        // that the link survives: the service is explicit that a failure at this
        // point leaves the token unspent and the operation replayable as is.
        return (
            <>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`Activation is unavailable`}
                </h1>
                <span className="text-sm">
                    {c('Activation refusal')
                        .t`Your link is still valid — the service that sets passwords is momentarily unreachable. Try again in a few minutes.`}
                </span>
                <Button size="large" fullWidth className="mt-6" onClick={onRetry}>
                    {c('Activation action').t`Try again`}
                </Button>
            </>
        );
    }

    if (refusal === null) {
        // Refused without a code we know: a network failure, a 500, a validation the
        // resolver raised before us. It is NOT worded as a link problem: that
        // would send someone chasing a new invitation over a passing incident: and
        // it does not claim the link is fine either, which nothing here established.
        return (
            <>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`Something went wrong`}
                </h1>
                <span className="text-sm">
                    {c('Activation refusal')
                        .t`Your password could not be set. Nothing has been changed on your account, so you can try again. If it keeps failing, contact your administrator.`}
                </span>
                <Button size="large" fullWidth className="mt-6" onClick={onRetry}>
                    {c('Activation action').t`Try again`}
                </Button>
            </>
        );
    }

    if (refusal === ActivationRefusal.ACCOUNT_DISABLED) {
        return (
            <>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`This access has been closed`}
                </h1>
                <span className="text-sm">
                    {c('Activation refusal')
                        .t`Your access was closed before this link was used. Your administrator can reopen it.`}
                </span>
            </>
        );
    }

    if (refusal === ActivationRefusal.EXPIRED_LINK) {
        return (
            <>
                <h1 className="text-2xl font-semibold text-text-norm pb-4">
                    {c('Activation title').t`This link has expired`}
                </h1>
                <span className="text-sm">
                    {c('Activation refusal')
                        .t`Invitation links are valid for a limited time. Ask your administrator to send you a new one — the previous link stops working as soon as they do.`}
                </span>
            </>
        );
    }

    return (
        <>
            <h1 className="text-2xl font-semibold text-text-norm pb-4">
                {c('Activation title').t`This link is not valid`}
            </h1>
            <span className="text-sm">
                {c('Activation refusal')
                    .t`This invitation link cannot be used. It may have been replaced by a more recent one — check that you opened the latest email, or ask your administrator for a new invitation.`}
            </span>
        </>
    );
};

export default ActivationPage;
